"use client"

import { useState, useCallback, useMemo } from "react"
import { getDatabase, ref, push, update, set } from "firebase/database"
import { displayProductDetails } from "./Utils"
import { useCurrentUid } from "../hooks/useCurrentUid"

const getIndianTimestamp = () => {
  const now = new Date()
  const istOffset = 5.5 * 60 * 60 * 1000
  const istTime = new Date(now.getTime() + istOffset)
  return istTime.toISOString().replace("T", " ").replace("Z", "")
}

const sendNegotiationNotification = async (userId, quoteId, brand, companyName, category) => {
  try {
    const db = getDatabase()
    const notificationRef = ref(db, `supplier_notifications/Negotiations/${userId}/${quoteId}`)
    const newNotification = {
      brand,
      category,
      title: "New Negotiation",
      message: `${companyName} has negotiated your quotation for ${brand} (${category}). Please review the details.`,
      gifUrl: "/Images/New notification.gif",
      timestamp: getIndianTimestamp(),
      seen: false,
    }
    await set(notificationRef, newNotification)
  } catch (error) {
    console.error("Error sending negotiation notification:", error)
  }
}

export default function ActiveSession({
  activeSession,
  endSession,
  toggleHistory,
  isMobile,
  MAX_NEGOTIATIONS,
}) {
  const userId = useCurrentUid()
  const [negotiationOpen, setNegotiationOpen] = useState(false)
  const [negotiationData, setNegotiationData] = useState({ counterOffer: "", message: "" })
  const [copySuccess, setCopySuccess] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Memoized calculations to prevent recalculation on every render
  const negotiationCount = useMemo(() => {
    return activeSession.negotiations ? Object.keys(activeSession.negotiations).length : 0
  }, [activeSession.negotiations])

  const isNegotiationLimitReached = useMemo(() => {
    return negotiationCount >= MAX_NEGOTIATIONS
  }, [negotiationCount, MAX_NEGOTIATIONS])

  const toggleNegotiation = useCallback(() => {
    setNegotiationOpen((prev) => !prev)
    if (!negotiationOpen) {
      setNegotiationData({ counterOffer: "", message: "" })
    }
  }, [negotiationOpen])

  const handleNegotiationChange = useCallback((field, value) => {
    setNegotiationData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }, [])

  const submitNegotiation = useCallback(async () => {
    if (!negotiationData.counterOffer.trim()) {
      alert("Please enter a counter offer price")
      return
    }

    if (isSubmitting) return // Prevent double submission

    setIsSubmitting(true)

    try {
      if (negotiationCount >= MAX_NEGOTIATIONS) {
        alert(`You have reached the maximum limit of ${MAX_NEGOTIATIONS} negotiations for this quotation.`)
        return
      }

      const db = getDatabase()
      const negotiationRef = ref(db, `quoteRequests/${userId}/${activeSession.id}/negotiations`)
      const newNegotiation = {
        counterOffer: negotiationData.counterOffer,
        message: negotiationData.message || "I would like to negotiate the price.",
        timestamp: new Date().toISOString(),
        status: "pending",
      }

      await push(negotiationRef, newNegotiation)

      const quoteRef = ref(db, `quoteRequests/${userId}/${activeSession.id}`)
      if (negotiationCount === MAX_NEGOTIATIONS - 1) {
        await update(quoteRef, { status: "completed" })
      } else {
        await update(quoteRef, { status: "negotiating" })
      }

      await sendNegotiationNotification(
        userId,
        activeSession.id,
        activeSession.brand,
        activeSession.companyName,
        activeSession.category || "cement",
      )

      setNegotiationOpen(false)
      setNegotiationData({ counterOffer: "", message: "" })
      alert("Negotiation submitted successfully!")
    } catch (error) {
      console.error("Error submitting negotiation:", error)
      alert("Error submitting negotiation: " + error.message)
    } finally {
      setIsSubmitting(false)
    }
  }, [negotiationData, negotiationCount, MAX_NEGOTIATIONS, userId, activeSession, isSubmitting])

  const copyToClipboard = useCallback(() => {
    let text = `QUOTATION DETAILS\n\n`
    text += `Category: ${activeSession.category || "Cement"}\n`
    text += `Brand: ${activeSession.brand}\n`

    if (activeSession.category === "cement" || !activeSession.category) {
      text += `Grade: ${activeSession.grade}\n`
      text += `Quantity: ${activeSession.quantity} ${activeSession.cementType === "bulker" ? "MT" : "bags"}\n`
      text += `Cement Type: ${activeSession.cementType || "bags"}\n`
    } else if (activeSession.category === "steel") {
      text += `Steel Grade: ${activeSession.steelGrade}\n`
      text += `Steel Type: ${activeSession.steelType}\n`
      text += `Quantity: ${activeSession.quantity} ${activeSession.quantityUnit || "tons"}\n`
    } else if (activeSession.category === "machinery") {
      text += `Machinery Type: ${activeSession.machineryType}\n`
      text += `Capacity: ${activeSession.machineryCapacity}\n`
      text += `Condition: ${activeSession.machineryCondition}\n`
      text += `Quantity: ${activeSession.quantity} ${activeSession.quantityUnit || "units"}\n`
    }

    text += `Site Address: ${activeSession.siteAddress}\n\n`
    text += `Contact Information:\n`
    text += `Name: ${activeSession.contactInfo.name}\n`
    text += `Phone: ${activeSession.contactInfo.phone}\n`
    text += `Email: ${activeSession.contactInfo.email}\n\n`

    if (activeSession.supplierReply) {
      text += `SUPPLIER'S RESPONSE\n\n`
      text += `Supplier Email: ${activeSession.supplierReply.supplierEmail}\n`
      if (activeSession.category === "cement" || !activeSession.category) {
        text += `Price Per ${activeSession.cementType === "bulker" ? "MT" : "Bag"}: ₹${activeSession.supplierReply.pricePerBag}\n`
      } else if (activeSession.category === "steel") {
        text += `Price Per Ton: ₹${activeSession.supplierReply.pricePerUnit}\n`
      } else if (activeSession.category === "machinery") {
        text += `Price Per Unit: ₹${activeSession.supplierReply.pricePerUnit}\n`
      }
      text += `Total Price: ₹${activeSession.supplierReply.totalPrice}\n`
      text += `Message: "${activeSession.supplierReply.message}"\n\n`
    }

    if (activeSession.negotiations && Object.keys(activeSession.negotiations).length > 0) {
      text += `NEGOTIATION HISTORY\n\n`
      Object.entries(activeSession.negotiations).forEach(([id, negotiation], index) => {
        text += `Negotiation ${index + 1}:\n`
        text += `Counter Offer: ₹${negotiation.counterOffer}\n`
        text += `Message: ${negotiation.message}\n`
        text += `Date: ${new Date(negotiation.timestamp).toLocaleString()}\n`
        text += `Status: ${negotiation.status}\n`
        if (negotiation.supplierResponse) {
          text += `Supplier Response: ${negotiation.supplierResponse.message}\n`
        }
        text += `\n`
      })
    }

    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopySuccess("Copied!")
        setTimeout(() => setCopySuccess(""), 2000)
      })
      .catch((err) => {
        console.error("Failed to copy: ", err)
        setCopySuccess("Failed to copy")
        setTimeout(() => setCopySuccess(""), 2000)
      })
  }, [activeSession])

  const toggleNegotiationHistory = useCallback(() => {
    const elem = document.getElementById(`negotiations-${activeSession.id}`)
    if (elem) {
      elem.classList.toggle("hidden")
    }
  }, [activeSession.id])

  return (
    <div className="active-session">
      <div className="active-session__header">
        <h3 className="active-session__title">Active Negotiation Session</h3>
        <div className="active-session__actions">
          <button className="button button--icon" onClick={copyToClipboard} title="Copy quotation details">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
            {copySuccess && <span className="copy-success">{copySuccess}</span>}
          </button>
          {isMobile && (
            <button className="button button--icon" onClick={toggleHistory}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9,18 15,12 9,6"></polyline>
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="quotation-card active-quotation-card">
        {/* Request Details */}
        <div className="quotation-card__header">
          <div className="quotation-card__title-area">
            <h4 className="quotation-card__title">
              Quote Request - {activeSession.category || "Cement"} - {activeSession.brand}
            </h4>
            <p className="quotation-card__date">Submitted: {activeSession.timestamp || activeSession.submittedAt}</p>
          </div>
          <div
            className={`badge ${
              activeSession.supplierReply
                ? "badge--success"
                : activeSession.status === "completed"
                  ? "badge--success"
                  : activeSession.status === "negotiating"
                    ? "badge--outline"
                    : "badge--secondary"
            }`}
          >
            {activeSession.supplierReply ? (
              "Responded"
            ) : activeSession.status === "completed" ? (
              <>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="badge__icon"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22,4 12,14.01 9,11.01"></polyline>
                </svg>
                Negotiation Complete
              </>
            ) : activeSession.status === "negotiating" ? (
              "Negotiating"
            ) : (
              "Pending Response"
            )}
          </div>
        </div>

        <div className="quotation-card__details">
          <div className="quotation-card__section">
            <h5 className="quotation-card__section-title">Request Details</h5>
            <ul className="quotation-card__list">
              {displayProductDetails(activeSession)}
              <li>
                <span className="quotation-card__label">Site Address:</span> {activeSession.siteAddress}
              </li>
            </ul>
          </div>

          <div className="quotation-card__section">
            <h5 className="quotation-card__section-title">Contact Information</h5>
            <ul className="quotation-card__list">
              <li>
                <span className="quotation-card__label">Name:</span> {activeSession.contactInfo.name}
              </li>
              <li>
                <span className="quotation-card__label">Phone:</span> {activeSession.contactInfo.phone}
              </li>
              
            </ul>
          </div>
        </div>

        {/* Supplier Response */}
        {activeSession.supplierReply ? (
          <div className="supplier-response">
            <h4 className="supplier-response__title">Supplier's Response</h4>
            <div className="supplier-response__content">
              <div className="supplier-response__section">
                <ul className="supplier-response__list">
                  {/* <li>
                    <span className="supplier-response__label">Supplier Email:</span>{" "}
                    {activeSession.supplierReply.supplierEmail}
                  </li> */}
                  <li>
                    <span className="supplier-response__label">
                      {activeSession.category === "cement" || !activeSession.category
                        ? `Price Per ${activeSession.cementType === "bulker" ? "MT" : "Bag"}`
                        : activeSession.category === "steel"
                          ? "Price Per Ton"
                          : "Price Per Unit"}
                      :
                    </span>{" "}
                    ₹{activeSession.supplierReply.pricePerBag || activeSession.supplierReply.pricePerUnit}
                  </li>
                  <li>
                    <span className="supplier-response__label">Total Price:</span>₹
                    {activeSession.supplierReply.totalPrice}
                  </li>
                </ul>
              </div>

              <div className="supplier-response__section">
                <p className="supplier-response__message-label">Message from Supplier:</p>
                <p className="supplier-response__message">"{activeSession.supplierReply.message}"</p>
              </div>
            </div>

            {/* Negotiation Button or Limit Message */}
            <div className="supplier-response__actions">
              {isNegotiationLimitReached ? (
                <div className="negotiation-limit-message">
                  <p>Negotiation limit reached (maximum {MAX_NEGOTIATIONS} negotiations).</p>
                  <p>If you want to negotiate further, please contact us directly:</p>
                  <div className="contact-info">
                    <p>Thank you for choosing us for your needs.</p>
                    <p>
                      Best regards,
                      <br />
                      Altrange
                      <br />
                      Contact: +91-9876980108
                    </p>
                  </div>
                </div>
              ) : (
                <button onClick={toggleNegotiation} className="button button--outline">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="button__icon"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                  {negotiationOpen ? "Cancel" : "Negotiate"}
                </button>
              )}
            </div>

            {/* Negotiation Form */}
            {negotiationOpen && !isNegotiationLimitReached && (
              <div className="negotiation-form negotiation-form--open">
                <h5 className="negotiation-form__title">Negotiate Price</h5>
                <div className="negotiation-form__fields">
                  <div className="form-group">
                    <label className="quote-form-label">
                      Your Counter Offer (per{" "}
                      {activeSession.category === "cement" || !activeSession.category
                        ? activeSession.cementType === "bulker"
                          ? "MT"
                          : "bag"
                        : activeSession.category === "steel"
                          ? "ton"
                          : "unit"}
                      )
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Enter your price"
                      value={negotiationData.counterOffer}
                      onChange={(e) => handleNegotiationChange("counterOffer", e.target.value)}
                      onWheel={(e) => e.target.blur()}
                    />
                  </div>
                  <div className="form-group">
                    <label className="quote-form-label">Message (Optional)</label>
                    <textarea
                      className="form-control"
                      placeholder="Explain your counter offer..."
                      value={negotiationData.message}
                      onChange={(e) => handleNegotiationChange("message", e.target.value)}
                    ></textarea>
                  </div>
                  <div className="negotiation-counter">
                    <span>
                      Negotiation {negotiationCount + 1} of {MAX_NEGOTIATIONS}
                    </span>
                  </div>
                  <button
                    onClick={submitNegotiation}
                    className="button button--primary button--full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Negotiation"}
                  </button>
                </div>
              </div>
            )}

            {/* Negotiation History */}
            {activeSession.negotiations && (
              <div className="negotiation-history">
                <div className="negotiation-history__header">
                  <h5 className="negotiation-history__title">Negotiation History</h5>
                  <button className="button button--icon" onClick={toggleNegotiationHistory}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="button__icon-small text-success"
                    >
                      <polyline points="6,9 12,15 18,9"></polyline>
                    </svg>
                  </button>
                </div>

                <div id={`negotiations-${activeSession.id}`} className="negotiation-history__content hidden">
                  {Object.entries(activeSession.negotiations).map(([negotiationId, negotiation]) => (
                    <div key={negotiationId} className="negotiation-item">
                      <div className="negotiation-item__header">
                        <div>
                          <p className="negotiation-item__price">Counter Offer: ₹{negotiation.counterOffer}</p>
                          <p className="negotiation-item__date">{new Date(negotiation.timestamp).toLocaleString()}</p>
                        </div>
                        <div
                          className={`badge ${
                            negotiation.status === "accepted"
                              ? "badge--success"
                              : negotiation.status === "rejected"
                                ? "badge--error"
                                : negotiation.supplierResponse
                                  ? "badge--success"
                                  : "badge--outline"
                          }`}
                        >
                          {negotiation.status === "accepted"
                            ? "Accepted"
                            : negotiation.status === "rejected"
                              ? "Rejected"
                              : negotiation.supplierResponse
                                ? "Responded"
                                : "Pending"}
                        </div>
                      </div>
                      <p className="negotiation-item__message">{negotiation.message}</p>

                      {/* Supplier Response to Negotiation */}
                      {negotiation.supplierResponse && (
                        <div className="negotiation-response">
                          <p className="negotiation-response__label">Supplier Response:</p>
                          <p className="negotiation-response__message">{negotiation.supplierResponse.message}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="no-response">
            <p className="no-response__message">
              Supplier has not responded to your quotation request yet. Please check back later.
            </p>
          </div>
        )}

        <div className="end-session-container">
          <button onClick={endSession} className="button button--danger">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="button__icon"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
            End Negotiation
          </button>
        </div>
      </div>
    </div>
  )
}
